import React, { useState, useEffect } from 'react';
import {
  MagnifyingGlassIcon, UserGroupIcon, CheckCircleIcon, XCircleIcon,
  EyeIcon, DocumentTextIcon, ArrowDownTrayIcon, PencilSquareIcon,
  LockClosedIcon, TrashIcon, KeyIcon, NoSymbolIcon, VideoCameraIcon,
} from '@heroicons/react/24/outline';
import { motion, AnimatePresence } from 'framer-motion';
import Layout from '../../components/Layout';
import api from '../../utils/api';
import toast from 'react-hot-toast';

const C = { teal:'#00B4D8', navy:'#0D1B2A', success:'#10B981', warning:'#F59E0B', error:'#EF4444', grey:'#6B7280', border:'#E5E7EB', bg:'#F0F7FA' };

/* ─── Modal shell ─────────────────────────────────────────────────────────── */
function ModalShell({ children, onClose, maxWidth = 520 }) {
  return (
    <div onClick={onClose} style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:1000, display:'flex', alignItems:'center', justifyContent:'center', padding:16, overflowY:'auto' }}>
      <motion.div onClick={e => e.stopPropagation()} initial={{ opacity:0, scale:0.95 }} animate={{ opacity:1, scale:1 }}
        style={{ background:'#fff', borderRadius:16, width:'100%', maxWidth, boxShadow:'0 24px 80px rgba(0,0,0,0.25)', maxHeight:'90vh', overflowY:'auto' }}>
        {children}
      </motion.div>
    </div>
  );
}

/* ─── Review / Full application modal ───────────────────────────────────── */
function ReviewModal({ tutor, onClose, onApprove, onDecline, acting }) {
  const [showDecline, setShowDecline] = useState(false);
  const [reason, setReason] = useState('');
  const specs = (tutor.specializations || []).map(s => s.replace(/_/g,' ').replace(/\b\w/g, l => l.toUpperCase()));

  const Section = ({ title, children }) => (
    <div style={{ marginBottom:20 }}>
      <div style={{ fontWeight:700, fontSize:11, textTransform:'uppercase', letterSpacing:1, color:C.grey, marginBottom:10, paddingBottom:6, borderBottom:`1px solid ${C.border}` }}>{title}</div>
      {children}
    </div>
  );
  const Row = ({ label, value }) => value ? (
    <div style={{ display:'flex', gap:8, marginBottom:6 }}>
      <span style={{ fontSize:13, color:C.grey, minWidth:130, flexShrink:0 }}>{label}:</span>
      <span style={{ fontSize:13, color:C.navy, fontWeight:500 }}>{value}</span>
    </div>
  ) : null;

  return (
    <ModalShell onClose={onClose} maxWidth={600}>
      <div style={{ padding:'24px 24px 0', display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
        <div style={{ display:'flex', gap:14, alignItems:'center' }}>
          <div style={{ width:56, height:56, borderRadius:'50%', background:'#8B5CF622', display:'flex', alignItems:'center', justifyContent:'center', fontWeight:800, color:'#8B5CF6', fontSize:22, flexShrink:0 }}>
            {(tutor.first_name?.[0] || tutor.email?.[0] || 'T').toUpperCase()}
          </div>
          <div>
            <div style={{ fontSize:20, fontWeight:800, color:C.navy }}>{tutor.first_name} {tutor.last_name}</div>
            <div style={{ fontSize:13, color:C.grey, marginTop:2 }}>{tutor.email}</div>
            {tutor.phone && <div style={{ fontSize:13, color:C.teal, marginTop:1 }}>{tutor.phone}</div>}
          </div>
        </div>
        <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:18, color:C.grey }}>✕</button>
      </div>
      <div style={{ padding:'20px 24px 24px' }}>
        {specs.length > 0 && (
          <div style={{ marginBottom:20, display:'flex', flexWrap:'wrap', gap:6 }}>
            {specs.map((s,i) => <span key={i} style={{ background:`${C.teal}15`, color:C.teal, padding:'4px 10px', borderRadius:20, fontSize:12, fontWeight:600 }}>{s}</span>)}
          </div>
        )}
        <Section title="Personal Details">
          <Row label="Native Language" value={tutor.native_language} />
          <Row label="Nationality" value={tutor.nationality} />
          <Row label="Date of Birth" value={tutor.date_of_birth} />
          <Row label="Applied" value={tutor.created_at ? new Date(tutor.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'long',year:'numeric'}) : null} />
        </Section>
        {tutor.bio && <Section title="About / Bio"><p style={{ margin:0, fontSize:13, color:C.navy, lineHeight:1.7 }}>{tutor.bio}</p></Section>}
        {Array.isArray(tutor.work_experience) && tutor.work_experience.length > 0 && (
          <Section title="Work Experience">
            {tutor.work_experience.map((w,i) => (
              <div key={i} style={{ marginBottom:10, padding:'10px 12px', background:C.bg, borderRadius:8 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>{w.title} {w.company && <span style={{ color:C.grey, fontWeight:400 }}>@ {w.company}</span>}</div>
                <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{w.start}{w.end ? ` — ${w.end}` : w.current ? ' — Present' : ''}</div>
                {w.description && <div style={{ fontSize:12, color:C.navy, marginTop:4 }}>{w.description}</div>}
              </div>
            ))}
          </Section>
        )}
        {Array.isArray(tutor.education) && tutor.education.length > 0 && (
          <Section title="Education">
            {tutor.education.map((e,i) => (
              <div key={i} style={{ marginBottom:10, padding:'10px 12px', background:C.bg, borderRadius:8 }}>
                <div style={{ fontWeight:700, fontSize:13, color:C.navy }}>{e.degree}</div>
                <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{e.institution}{e.year ? `, ${e.year}` : ''}</div>
                {e.field && <div style={{ fontSize:12, color:C.teal, marginTop:2 }}>{e.field}</div>}
              </div>
            ))}
          </Section>
        )}
        {Array.isArray(tutor.qualifications) && tutor.qualifications.length > 0 && (
          <Section title="Qualifications">
            {tutor.qualifications.map((q,i) => (
              <div key={i} style={{ marginBottom:8, display:'flex', alignItems:'center', gap:10, padding:'8px 12px', background:C.bg, borderRadius:8 }}>
                <CheckCircleIcon style={{ width:16, color:C.success, flexShrink:0 }} />
                <div><div style={{ fontWeight:700, fontSize:13, color:C.navy }}>{q.name}</div><div style={{ fontSize:12, color:C.grey }}>{q.issuer}{q.year ? ` · ${q.year}` : ''}</div></div>
              </div>
            ))}
          </Section>
        )}
        {(tutor.cv_url || tutor.qualification_doc_url || tutor.profile_photo_url) && (
          <Section title="Documents">
            <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
              {tutor.cv_url && <a href={tutor.cv_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#EFF6FF', borderRadius:8, textDecoration:'none', color:'#1D4ED8', fontWeight:600, fontSize:13 }}><DocumentTextIcon style={{ width:18 }} /> View / Download CV <ArrowDownTrayIcon style={{ width:14, marginLeft:'auto' }} /></a>}
              {tutor.qualification_doc_url && <a href={tutor.qualification_doc_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#F0FDF4', borderRadius:8, textDecoration:'none', color:'#15803D', fontWeight:600, fontSize:13 }}><DocumentTextIcon style={{ width:18 }} /> View Qualification Certificate <ArrowDownTrayIcon style={{ width:14, marginLeft:'auto' }} /></a>}
              {tutor.profile_photo_url && <a href={tutor.profile_photo_url} target="_blank" rel="noreferrer" style={{ display:'flex', alignItems:'center', gap:10, padding:'10px 14px', background:'#FDF4FF', borderRadius:8, textDecoration:'none', color:'#7C3AED', fontWeight:600, fontSize:13 }}><EyeIcon style={{ width:18 }} /> View Profile Photo</a>}
            </div>
          </Section>
        )}
        {showDecline && (
          <div style={{ marginBottom:16, padding:16, background:'#FEF2F2', borderRadius:10, border:`1px solid ${C.error}30` }}>
            <div style={{ fontWeight:700, fontSize:13, color:C.error, marginBottom:8 }}>Decline Reason (optional)</div>
            <textarea value={reason} onChange={e => setReason(e.target.value)} placeholder="e.g. Not currently recruiting" rows={3}
              style={{ width:'100%', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', fontSize:13, resize:'none', boxSizing:'border-box' }} />
          </div>
        )}
        <div style={{ display:'flex', gap:10, marginTop:4 }}>
          {!showDecline ? (
            <>
              <button onClick={() => { onApprove(tutor.id, `${tutor.first_name} ${tutor.last_name}`); onClose(); }} disabled={acting}
                style={{ flex:2, padding:'12px 0', background:C.success, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:8 }}>
                <CheckCircleIcon style={{ width:18 }} /> Accept Application
              </button>
              <button onClick={() => setShowDecline(true)} disabled={acting}
                style={{ flex:1, padding:'12px 0', background:'#FEF2F2', color:C.error, border:`1px solid ${C.error}40`, borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                Decline
              </button>
            </>
          ) : (
            <>
              <button onClick={() => setShowDecline(false)} style={{ flex:1, padding:'12px 0', background:'#F3F4F6', border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>Back</button>
              <button onClick={() => { onDecline(tutor, reason); onClose(); }} disabled={acting}
                style={{ flex:2, padding:'12px 0', background:C.error, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
                Confirm Decline
              </button>
            </>
          )}
        </div>
      </div>
    </ModalShell>
  );
}

/* ─── Edit tutor modal ───────────────────────────────────────────────────── */
function EditModal({ tutor, onClose, onSaved }) {
  const [form, setForm] = useState({
    first_name: tutor.first_name || '',
    last_name:  tutor.last_name  || '',
    email:      tutor.email      || '',
    phone:      tutor.phone      || '',
    bio:        tutor.bio        || '',
    native_language: tutor.native_language || '',
    nationality:     tutor.nationality     || '',
  });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/api/admin/tutors/${tutor.id}/edit`, form);
      toast.success('Tutor profile updated');
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to save'); }
    setSaving(false);
  };

  const F = ({ label, field, type='text', rows }) => (
    <div style={{ marginBottom:14 }}>
      <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>{label}</label>
      {rows ? (
        <textarea rows={rows} value={form[field]} onChange={e => setForm({...form, [field]:e.target.value})}
          style={{ width:'100%', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', fontSize:13, resize:'vertical', boxSizing:'border-box' }} />
      ) : (
        <input type={type} value={form[field]} onChange={e => setForm({...form, [field]:e.target.value})}
          style={{ width:'100%', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', fontSize:13, boxSizing:'border-box' }} />
      )}
    </div>
  );

  return (
    <ModalShell onClose={onClose}>
      <div style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:20 }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:C.navy }}>Edit Tutor Profile</h3>
          <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:18, color:C.grey }}>✕</button>
        </div>
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:'0 12px' }}>
          <F label="First Name" field="first_name" />
          <F label="Last Name" field="last_name" />
        </div>
        <F label="Email" field="email" type="email" />
        <F label="Phone" field="phone" type="tel" />
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(min(100%, 260px), 1fr))', gap:'0 12px' }}>
          <F label="Native Language" field="native_language" />
          <F label="Nationality" field="nationality" />
        </div>
        <F label="Bio" field="bio" rows={3} />
        <div style={{ display:'flex', gap:10, marginTop:4 }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px 0', background:'#F3F4F6', border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{ flex:2, padding:'11px 0', background:C.navy, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
            {saving ? 'Saving…' : 'Save Changes'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ─── Reset password modal ───────────────────────────────────────────────── */
function ResetPasswordModal({ tutor, onClose }) {
  const [pwd, setPwd] = useState('');
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const save = async () => {
    if (pwd.length < 8) { toast.error('Password must be at least 8 characters'); return; }
    if (pwd !== confirm) { toast.error('Passwords do not match'); return; }
    setSaving(true);
    try {
      await api.post(`/api/admin/tutors/${tutor.id}/reset-password`, { new_password: pwd });
      toast.success(`Password reset for ${tutor.first_name}. Email notification sent.`);
      onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to reset'); }
    setSaving(false);
  };

  return (
    <ModalShell onClose={onClose} maxWidth={400}>
      <div style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:C.navy }}>Reset Password</h3>
          <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:18, color:C.grey }}>✕</button>
        </div>
        <p style={{ margin:'0 0 20px', fontSize:13, color:C.grey }}>Set a new password for <strong>{tutor.first_name} {tutor.last_name}</strong>. They'll receive an email with the new credentials.</p>
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>New Password</label>
          <input type="password" value={pwd} onChange={e => setPwd(e.target.value)} placeholder="Min. 8 characters"
            style={{ width:'100%', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', fontSize:14, boxSizing:'border-box' }} />
        </div>
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Confirm Password</label>
          <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Repeat password"
            style={{ width:'100%', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', fontSize:14, boxSizing:'border-box',
              borderColor: confirm && pwd !== confirm ? C.error : C.border }} />
          {confirm && pwd !== confirm && <div style={{ fontSize:12, color:C.error, marginTop:4 }}>Passwords don't match</div>}
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px 0', background:'#F3F4F6', border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving}
            style={{ flex:2, padding:'11px 0', background:'#7C3AED', color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
            {saving ? 'Resetting…' : '🔑 Reset Password'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ─── Block modal ────────────────────────────────────────────────────────── */
function BlockModal({ tutor, onClose, onDone }) {
  const [reason, setReason] = useState('');
  const [saving, setSaving] = useState(false);

  const doBlock = async () => {
    setSaving(true);
    try {
      await api.post(`/api/admin/tutors/${tutor.id}/block`, { action:'block', reason });
      toast.success(`${tutor.first_name} has been blocked`);
      onDone(); onClose();
    } catch (e) { toast.error('Failed to block'); }
    setSaving(false);
  };

  return (
    <ModalShell onClose={onClose} maxWidth={400}>
      <div style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:C.navy }}>Block Tutor</h3>
          <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:18, color:C.grey }}>✕</button>
        </div>
        <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>
          Blocking <strong>{tutor.first_name} {tutor.last_name}</strong> will prevent them from logging in. You can unblock them at any time.
        </p>
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Reason (optional)</label>
          <textarea value={reason} onChange={e => setReason(e.target.value)} rows={3} placeholder="e.g. Pending investigation"
            style={{ width:'100%', border:`1px solid ${C.border}`, borderRadius:8, padding:'10px 12px', fontSize:13, resize:'none', boxSizing:'border-box' }} />
        </div>
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px 0', background:'#F3F4F6', border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>Cancel</button>
          <button onClick={doBlock} disabled={saving}
            style={{ flex:2, padding:'11px 0', background:C.warning, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
            {saving ? 'Blocking…' : '🚫 Block Tutor'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ─── Delete confirm modal ───────────────────────────────────────────────── */
function DeleteModal({ tutor, onClose, onDone }) {
  const [confirm, setConfirm] = useState('');
  const [saving, setSaving] = useState(false);

  const doDelete = async () => {
    setSaving(true);
    try {
      await api.delete(`/api/admin/tutors/${tutor.id}/delete`);
      toast.success(`${tutor.first_name} removed from school`);
      onDone(); onClose();
    } catch (e) { toast.error('Failed to remove'); }
    setSaving(false);
  };

  return (
    <ModalShell onClose={onClose} maxWidth={400}>
      <div style={{ padding:24 }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:C.error }}>Remove Tutor</h3>
          <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:18, color:C.grey }}>✕</button>
        </div>
        <p style={{ margin:'0 0 16px', fontSize:13, color:C.grey }}>
          This will remove <strong>{tutor.first_name} {tutor.last_name}</strong> from your school and disable their account. This action cannot be easily undone.
        </p>
        <div style={{ background:'#FEF2F2', border:`1px solid ${C.error}30`, borderRadius:8, padding:'12px 14px', marginBottom:20, fontSize:13, color:C.error }}>
          ⚠️ Type <strong>DELETE</strong> below to confirm
        </div>
        <input value={confirm} onChange={e => setConfirm(e.target.value)} placeholder="Type DELETE to confirm"
          style={{ width:'100%', border:`1px solid ${confirm==='DELETE'?C.error:C.border}`, borderRadius:8, padding:'10px 12px', fontSize:14, boxSizing:'border-box', marginBottom:20 }} />
        <div style={{ display:'flex', gap:10 }}>
          <button onClick={onClose} style={{ flex:1, padding:'11px 0', background:'#F3F4F6', border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>Cancel</button>
          <button onClick={doDelete} disabled={saving || confirm !== 'DELETE'}
            style={{ flex:2, padding:'11px 0', background:confirm==='DELETE'?C.error:'#D1D5DB', color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor:confirm==='DELETE'?'pointer':'default' }}>
            {saving ? 'Removing…' : '🗑 Remove Tutor'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}


/* ─── Detect platform from URL ──────────────────────────────────────────── */
function detectPlatform(url) {
  if (!url) return 'google_meet';
  if (url.includes('meet.google')) return 'google_meet';
  if (url.includes('zoom.us'))     return 'zoom';
  if (url.includes('teams.microsoft') || url.includes('teams.live')) return 'teams';
  if (url.includes('skype.com') || url.includes('join.skype')) return 'skype';
  return 'google_meet';
}

const PLATFORM_META = {
  google_meet: { label: 'Google Meet', icon: '📹', color: '#34A853', bg: '#F0FDF4' },
  zoom:        { label: 'Zoom',        icon: '🎥', color: '#2D8CFF', bg: '#EFF6FF' },
  teams:       { label: 'Teams',       icon: '💼', color: '#5B5EA6', bg: '#F5F3FF' },
  skype:       { label: 'Skype',       icon: '📞', color: '#00AFF0', bg: '#EFF6FF' },
};

/* ─── Video Room Modal ───────────────────────────────────────────────────── */
function VideoRoomModal({ tutor, onClose, onSaved }) {
  const [link,     setLink]    = useState(tutor.online_link     || '');
  const [platform, setPlatform]= useState(tutor.online_platform || 'google_meet');
  const [saving,   setSaving]  = useState(false);
  const [clearing, setClearing]= useState(false);

  // Auto-detect platform from URL as user types
  const handleLinkChange = (val) => {
    setLink(val);
    const detected = detectPlatform(val);
    if (detected) setPlatform(detected);
  };

  const save = async () => {
    if (!link.trim()) { return; }
    setSaving(true);
    try {
      await api.post(`/api/admin/tutors/${tutor.id}/assign-video-room`, { online_link: link.trim(), online_platform: platform });
      toast.success(`Video room assigned to ${tutor.first_name}`);
      onSaved(); onClose();
    } catch (e) { toast.error(e.response?.data?.error || 'Failed to assign room'); }
    setSaving(false);
  };

  const clear = async () => {
    setClearing(true);
    try {
      await api.post(`/api/admin/tutors/${tutor.id}/assign-video-room`, { clear: true });
      toast.success(`Video room cleared for ${tutor.first_name}`);
      onSaved(); onClose();
    } catch (e) { toast.error('Failed to clear room'); }
    setClearing(false);
  };

  const pm = PLATFORM_META[platform] || PLATFORM_META.google_meet;

  return (
    <ModalShell onClose={onClose} maxWidth={480}>
      <div style={{ padding: 24 }}>
        {/* Header */}
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:6 }}>
          <h3 style={{ margin:0, fontSize:18, fontWeight:700, color:C.navy }}>
            📹 Assign Video Room
          </h3>
          <button onClick={onClose} style={{ background:'#F3F4F6', border:'none', borderRadius:8, padding:'6px 10px', cursor:'pointer', fontSize:18, color:C.grey }}>✕</button>
        </div>
        <p style={{ margin:'0 0 20px', fontSize:13, color:C.grey }}>
          Assign a school-owned Google Meet link to <strong>{tutor.first_name} {tutor.last_name}</strong>. The tutor will see this link on their dashboard but cannot change it.
        </p>

        {/* Current link status */}
        {tutor.online_link && (
          <div style={{ background:'#F0FDF4', border:'1px solid #BBF7D0', borderRadius:10, padding:'12px 14px', marginBottom:16, display:'flex', alignItems:'center', gap:10 }}>
            <span style={{ fontSize:20 }}>{PLATFORM_META[tutor.online_platform]?.icon || '📹'}</span>
            <div style={{ flex:1, minWidth:0 }}>
              <div style={{ fontSize:12, fontWeight:700, color:'#065F46', marginBottom:2 }}>Currently assigned:</div>
              <a href={tutor.online_link} target="_blank" rel="noreferrer"
                style={{ fontSize:12, color:'#0D9488', textDecoration:'none', wordBreak:'break-all' }}>{tutor.online_link}</a>
            </div>
          </div>
        )}

        {/* Info box */}
        <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:8, padding:'10px 14px', marginBottom:18, fontSize:12, color:'#1D4ED8' }}>
          <strong>How to create a Google Meet room:</strong><br />
          Go to calendar.google.com → Create event → Add Google Meet → copy the meet link → paste it below.
          This link is owned by the school account and remains active until you delete the calendar event.
        </div>

        {/* Platform selector */}
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>Platform</label>
          <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
            {Object.entries(PLATFORM_META).map(([key, meta]) => (
              <button key={key} onClick={() => setPlatform(key)} style={{
                padding:'7px 14px', borderRadius:20, border:`2px solid ${platform===key ? meta.color : C.border}`,
                background: platform===key ? meta.bg : '#fff', color: platform===key ? meta.color : C.grey,
                fontWeight: platform===key ? 700 : 500, fontSize:13, cursor:'pointer',
              }}>
                {meta.icon} {meta.label}
              </button>
            ))}
          </div>
        </div>

        {/* Link input */}
        <div style={{ marginBottom:20 }}>
          <label style={{ fontSize:13, fontWeight:600, color:'#374151', display:'block', marginBottom:6 }}>
            {pm.icon} Room Link (from school account)
          </label>
          <input
            value={link} onChange={e => handleLinkChange(e.target.value)}
            placeholder={`e.g. https://meet.google.com/abc-defg-hij`}
            style={{ width:'100%', border:`1px solid ${link && !link.startsWith('http') ? C.error : C.border}`, borderRadius:8, padding:'10px 12px', fontSize:13, boxSizing:'border-box' }}
          />
          {link && !link.startsWith('http') && <div style={{ fontSize:11, color:C.error, marginTop:4 }}>Link must start with https://</div>}
        </div>

        {/* Buttons */}
        <div style={{ display:'flex', gap:10 }}>
          {tutor.online_link && (
            <button onClick={clear} disabled={clearing}
              style={{ flex:1, padding:'11px 0', background:'#FEF2F2', color:C.error, border:`1px solid ${C.error}30`, borderRadius:10, fontWeight:700, fontSize:14, cursor:'pointer' }}>
              {clearing ? 'Clearing…' : '🗑 Clear Room'}
            </button>
          )}
          <button onClick={onClose} style={{ flex:1, padding:'11px 0', background:'#F3F4F6', border:'none', borderRadius:10, fontWeight:600, fontSize:14, cursor:'pointer' }}>Cancel</button>
          <button onClick={save} disabled={saving || !link.trim() || !link.startsWith('http')}
            style={{ flex:2, padding:'11px 0', background: (!link.trim() || !link.startsWith('http')) ? '#D1D5DB' : C.teal, color:'#fff', border:'none', borderRadius:10, fontWeight:700, fontSize:14, cursor: (!link.trim() || !link.startsWith('http')) ? 'default' : 'pointer' }}>
            {saving ? 'Saving…' : '📹 Assign Room'}
          </button>
        </div>
      </div>
    </ModalShell>
  );
}

/* ══════════════════════════════════════════════════════════════════════════ */
export default function AdminTutors() {
  const [tutors,  setTutors]  = useState([]);
  const [loading, setLoading] = useState(true);
  const [search,  setSearch]  = useState('');
  const [tab,     setTab]     = useState('pending');
  const [tenant,  setTenant]  = useState(null);
  const [acting,  setActing]  = useState(false);

  // Modal state
  const [reviewTutor,  setReviewTutor]  = useState(null);
  const [editTutor,    setEditTutor]    = useState(null);
  const [resetTutor,   setResetTutor]   = useState(null);
  const [blockTutor,   setBlockTutor]   = useState(null);
  const [deleteTutor,  setDeleteTutor]  = useState(null);
  const [videoRoomTutor, setVideoRoomTutor] = useState(null);

  const load = () => {
    setLoading(true);
    Promise.all([api.get('/api/admin/tutors'), api.get('/api/admin/dashboard')])
      .then(([t, d]) => { setTutors(t.data?.tutors || []); setTenant(d.data?.tenant); })
      .catch(() => {})
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  const approveTutor = async (id, name) => {
    setActing(true);
    try { await api.post(`/api/admin/tutors/${id}/approve`); toast.success(`${name} approved`); load(); }
    catch { toast.error('Failed to approve'); }
    setActing(false);
  };

  const declineTutor = async (tutor, reason) => {
    setActing(true);
    try { await api.post(`/api/admin/tutors/${tutor.id}/decline`, { reason }); toast.success('Application declined'); load(); }
    catch { toast.error('Failed to decline'); }
    setActing(false);
  };

  const unblockTutor = async (tutor) => {
    try { await api.post(`/api/admin/tutors/${tutor.id}/block`, { action:'unblock' }); toast.success(`${tutor.first_name} unblocked`); load(); }
    catch { toast.error('Failed to unblock'); }
  };

  const pending  = tutors.filter(t => !t.is_approved && t.application_status !== 'declined' && t.account_status !== 'declined' && t.account_status !== 'deleted');
  const approved = tutors.filter(t => t.is_approved && t.account_status !== 'deleted');
  const blocked  = approved.filter(t => t.account_status === 'blocked');
  const declined = tutors.filter(t => t.application_status === 'declined' || t.account_status === 'declined');

  const listMap  = { pending, approved, declined };
  const filtered = (listMap[tab] || []).filter(t =>
    `${t.first_name} ${t.last_name} ${t.email}`.toLowerCase().includes(search.toLowerCase())
  );

  const TABS = [
    { key:'pending',  label:'Applications', count: pending.length,  color: C.warning },
    { key:'approved', label:'Active Tutors', count: approved.length, color: C.success },
    { key:'declined', label:'Declined',      count: declined.length, color: C.grey },
  ];

  return (
    <Layout role="tenant_admin" tenantColor={tenant?.primary_colour} tenantName={tenant?.school_name}>

      <AnimatePresence>
        {reviewTutor  && <ReviewModal  tutor={reviewTutor}  onClose={() => setReviewTutor(null)}  onApprove={approveTutor} onDecline={declineTutor} acting={acting} />}
        {editTutor    && <EditModal    tutor={editTutor}    onClose={() => setEditTutor(null)}    onSaved={load} />}
        {resetTutor   && <ResetPasswordModal tutor={resetTutor} onClose={() => setResetTutor(null)} />}
        {blockTutor   && <BlockModal   tutor={blockTutor}   onClose={() => setBlockTutor(null)}   onDone={load} />}
        {deleteTutor  && <DeleteModal  tutor={deleteTutor}  onClose={() => setDeleteTutor(null)}  onDone={load} />}
        {videoRoomTutor && <VideoRoomModal tutor={videoRoomTutor} onClose={() => setVideoRoomTutor(null)} onSaved={load} />}
      </AnimatePresence>

      <div style={{ marginBottom:24 }}>
        <h2 style={{ margin:'0 0 4px', fontSize:22, fontWeight:700, color:C.navy }}>Tutor Applications</h2>
        <p style={{ margin:0, color:C.grey, fontSize:14 }}>Review and approve tutors who apply to join your school.</p>
      </div>

      <div style={{ background:'#EFF6FF', border:'1px solid #BFDBFE', borderRadius:10, padding:'12px 16px', marginBottom:24, fontSize:13, color:'#1D4ED8' }}>
        <strong>How it works:</strong> Tutors apply via the public signup page. Review their full application before accepting or declining.
      </div>

      {/* Tabs + Search */}
      <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:20, flexWrap:'wrap', gap:12 }}>
        <div style={{ display:'flex', gap:8, flexWrap:'wrap' }}>
          {TABS.map(t => (
            <button key={t.key} onClick={() => setTab(t.key)} style={{
              padding:'8px 16px', borderRadius:20, border:'none', cursor:'pointer', fontWeight:600, fontSize:13,
              background: tab===t.key ? C.navy : '#fff', color: tab===t.key ? '#fff' : C.grey,
              boxShadow: tab===t.key ? 'none' : '0 1px 4px rgba(0,0,0,0.08)',
            }}>
              {t.label}
              {t.count > 0 && <span style={{ marginLeft:6, padding:'2px 7px', borderRadius:10, fontSize:11, fontWeight:700, background: tab===t.key ? 'rgba(255,255,255,0.25)' : t.color+'18', color: tab===t.key ? '#fff' : t.color }}>{t.count}</span>}
            </button>
          ))}
        </div>
        <div style={{ position:'relative' }}>
          <MagnifyingGlassIcon style={{ width:15, position:'absolute', left:10, top:'50%', transform:'translateY(-50%)', color:C.grey }} />
          <input placeholder="Search tutors…" value={search} onChange={e => setSearch(e.target.value)}
            style={{ padding:'8px 12px 8px 30px', border:`1px solid ${C.border}`, borderRadius:8, fontSize:13, width:220 }} />
        </div>
      </div>

      {/* List */}
      {loading ? (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
          {[1,2,3].map(i => <div key={i} style={{ height:200, background:'#F3F4F6', borderRadius:12 }} />)}
        </div>
      ) : filtered.length === 0 ? (
        <div style={{ textAlign:'center', padding:'60px 0', color:C.grey, background:'#fff', borderRadius:12 }}>
          <UserGroupIcon style={{ width:40, margin:'0 auto 12px', display:'block', opacity:0.3 }} />
          <div style={{ fontWeight:700, fontSize:16, marginBottom:8 }}>
            {tab==='pending' ? 'No pending applications' : tab==='approved' ? 'No active tutors yet' : 'No declined applications'}
          </div>
          {tab==='pending' && <p style={{ margin:0, fontSize:13 }}>When tutors apply via the signup page, they'll appear here.</p>}
        </div>
      ) : (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(320px,1fr))', gap:16 }}>
          <AnimatePresence>
            {filtered.map((t) => (
              <motion.div key={t.id} initial={{ opacity:0, y:8 }} animate={{ opacity:1, y:0 }} className="card" style={{ padding:20 }}>
                {/* Card header */}
                <div style={{ display:'flex', alignItems:'flex-start', gap:12, marginBottom:14 }}>
                  {t.profile_photo_url
                    ? <img src={t.profile_photo_url} alt={t.first_name} style={{ width:48, height:48, borderRadius:'50%', objectFit:'cover', flexShrink:0 }} />
                    : <div style={{ width:48, height:48, borderRadius:'50%', background:'#8B5CF622', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center', fontWeight:700, color:'#8B5CF6', fontSize:20 }}>
                        {(t.first_name?.[0] || t.email?.[0] || 'T').toUpperCase()}
                      </div>
                  }
                  <div style={{ flex:1, minWidth:0 }}>
                    <div style={{ fontWeight:700, fontSize:15, color:C.navy }}>{t.first_name} {t.last_name}</div>
                    <div style={{ fontSize:12, color:C.grey, marginTop:2 }}>{t.email}</div>
                    <div style={{ fontSize:12, color:C.teal, marginTop:4 }}>
                      {(t.specializations||[]).slice(0,3).map(s=>s.replace(/_/g,' ').replace(/\b\w/g,l=>l.toUpperCase()).replace(/\bIelts\b/g,'IELTS').replace(/\bAi\b/g,'AI')).join(' · ')}
                      {(t.specializations||[]).length > 3 && ` +${(t.specializations||[]).length-3} more`}
                    </div>
                  </div>
                  <div style={{ padding:'3px 10px', borderRadius:20, fontSize:11, fontWeight:700, whiteSpace:'nowrap',
                    background: t.account_status==='blocked' ? '#FEF3C7' : tab==='approved' ? '#ECFDF5' : tab==='declined' ? '#FEF2F2' : '#FEF3C7',
                    color:      t.account_status==='blocked' ? C.warning  : tab==='approved' ? C.success  : tab==='declined' ? C.error   : C.warning }}>
                    {t.account_status==='blocked' ? '🚫 Blocked' : tab==='approved' ? '✅ Active' : tab==='declined' ? '✗ Declined' : '⏳ Pending'}
                  </div>
                </div>

                {t.bio && <p style={{ margin:'0 0 12px', fontSize:12, color:C.grey, lineHeight:1.6, display:'-webkit-box', WebkitLineClamp:2, WebkitBoxOrient:'vertical', overflow:'hidden' }}>{t.bio}</p>}

                <div style={{ fontSize:11, color:C.grey, marginBottom:14 }}>
                  Applied: {t.created_at ? new Date(t.created_at).toLocaleDateString('en-GB',{day:'numeric',month:'short',year:'numeric'}) : '—'}
                  {t.tutor_id_number && <span style={{ marginLeft:12, background:`${C.teal}15`, color:C.teal, padding:'2px 8px', borderRadius:10, fontWeight:600 }}>{t.tutor_id_number}</span>}
                </div>

                {/* Review button — always visible */}
                <button onClick={() => setReviewTutor(t)}
                  style={{ width:'100%', padding:'9px 0', background:`${C.navy}08`, color:C.navy, border:`1px solid ${C.border}`, borderRadius:8, fontWeight:600, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6, marginBottom:8 }}>
                  <EyeIcon style={{ width:15 }} /> Review Full Application
                </button>

                {/* ── PENDING actions ── */}
                {tab === 'pending' && (
                  <div style={{ display:'flex', gap:8 }}>
                    <button onClick={() => approveTutor(t.id, `${t.first_name} ${t.last_name}`)} disabled={acting}
                      style={{ flex:1, padding:'9px 0', background:C.success, color:'#fff', border:'none', borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                      <CheckCircleIcon style={{ width:16 }} /> Accept
                    </button>
                    <button onClick={() => setReviewTutor({...t, _startDecline:true})} disabled={acting}
                      style={{ flex:1, padding:'9px 0', background:'#FEF2F2', color:C.error, border:`1px solid ${C.error}30`, borderRadius:8, fontWeight:700, fontSize:13, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                      <XCircleIcon style={{ width:16 }} /> Decline
                    </button>
                  </div>
                )}

                {/* ── APPROVED actions ── */}
                {tab === 'approved' && (
                  <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                    <div style={{ display:'flex', gap:8 }}>
                      <button onClick={() => setEditTutor(t)}
                        style={{ flex:1, padding:'8px 0', background:'#EFF6FF', color:'#1D4ED8', border:'1px solid #BFDBFE', borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                        <PencilSquareIcon style={{ width:14 }} /> Edit
                      </button>
                      <button onClick={() => setResetTutor(t)}
                        style={{ flex:1, padding:'8px 0', background:'#F5F3FF', color:'#7C3AED', border:'1px solid #DDD6FE', borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                        <KeyIcon style={{ width:14 }} /> Reset Pwd
                      </button>
                    </div>
                    {/* Video Room button - school assigns Google Meet link */}
                    <button onClick={() => setVideoRoomTutor(t)}
                      style={{ width:'100%', padding:'8px 0', background: t.online_link ? '#F0FDF4' : '#F0F7FA', color: t.online_link ? '#065F46' : C.teal, border:`1px solid ${t.online_link ? '#BBF7D0' : C.teal+'40'}`, borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:6 }}>
                      <VideoCameraIcon style={{ width:14 }} />
                      {t.online_link ? '📹 Room Assigned — Update' : '📹 Assign Video Room'}
                    </button>
                    <div style={{ display:'flex', gap:8 }}>
                      {t.account_status === 'blocked' ? (
                        <button onClick={() => unblockTutor(t)}
                          style={{ flex:1, padding:'8px 0', background:'#F0FDF4', color:C.success, border:`1px solid ${C.success}40`, borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                          <CheckCircleIcon style={{ width:14 }} /> Unblock
                        </button>
                      ) : (
                        <button onClick={() => setBlockTutor(t)}
                          style={{ flex:1, padding:'8px 0', background:'#FFFBEB', color:C.warning, border:`1px solid ${C.warning}40`, borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                          <NoSymbolIcon style={{ width:14 }} /> Block
                        </button>
                      )}
                      <button onClick={() => setDeleteTutor(t)}
                        style={{ flex:1, padding:'8px 0', background:'#FEF2F2', color:C.error, border:`1px solid ${C.error}30`, borderRadius:8, fontWeight:600, fontSize:12, cursor:'pointer', display:'flex', alignItems:'center', justifyContent:'center', gap:5 }}>
                        <TrashIcon style={{ width:14 }} /> Remove
                      </button>
                    </div>
                  </div>
                )}

                {tab === 'declined' && (
                  <div style={{ display:'flex', alignItems:'center', gap:8, marginTop:4 }}>
                    <XCircleIcon style={{ width:16, color:C.error }} />
                    <span style={{ fontSize:12, color:C.error, fontWeight:600 }}>Application not successful</span>
                  </div>
                )}
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </Layout>
  );
}
